/*
  Mobile product page behaviour: the sticky buy bar, and the description clamp.

  Both are additive. With this file blocked the page still works: the bar stays
  hidden, and the description shows in full.
*/

if (!customElements.get('mobile-buy-bar')) {
  customElements.define(
    'mobile-buy-bar',
    class MobileBuyBar extends HTMLElement {
      connectedCallback() {
        this.priceTarget = this.querySelector('[data-bar-price]');
        this.button = this.querySelector('[data-bar-submit]');
        this.source = document.querySelector('[id^="price-"]');
        this.mainSubmit = document.querySelector('[id^="ProductSubmitButton-"]');
        this.picker = document.querySelector('variant-selects');
        this.notification = document.getElementById('cart-notification');

        this.button?.addEventListener('click', () => this.toOptions());

        this.watchVisibility();
        this.watchVariant();
        this.watchNotification();
      }

      disconnectedCallback() {
        this.visibility?.disconnect();
        this.variant?.disconnect();
        this.notice?.disconnect();
      }

      /* Back to the options rather than straight into the cart. A lead is a
         size and a length decision and this bar shows neither, so adding from
         here would commit whatever happened to be selected. */
      toOptions() {
        const target = this.picker || this.mainSubmit;
        if (!target) return;

        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
        });

        const first = target.querySelector('input[type="radio"], select, button');
        if (first) window.setTimeout(() => first.focus({ preventScroll: true }), 500);
      }

      /* Shown once the real button has been scrolled past, not merely while it
         is off screen: below the fold the buyer has not reached the options
         yet, and a bar there pre-empts the choice. */
      watchVisibility() {
        if (!this.mainSubmit) return;

        this.visibility = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = entry.boundingClientRect.top < 0;
              this.toggle(!entry.isIntersecting && scrolledPast);
            });
          },
          { threshold: 0 }
        );

        this.visibility.observe(this.mainSubmit);
      }

      toggle(show) {
        this.wantsToShow = show;
        this.render();
      }

      /* The cart notification is a dialog that lands in the same corner. Two
         fixed panels stacked on each other is a stack of furniture, so the bar
         stands down while it is open. */
      watchNotification() {
        if (!this.notification) return;

        this.notice = new MutationObserver(() => {
          this.notificationOpen = this.notification.classList.contains('active');
          this.render();
        });

        this.notice.observe(this.notification, { attributes: true, attributeFilter: ['class'] });
      }

      render() {
        const show = this.wantsToShow && !this.notificationOpen;
        this.classList.toggle('mobile-buy-bar--visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
      }

      /* The theme re-renders the price when the variant changes, so mirroring
         that node keeps the bar honest without reimplementing variant lookup. */
      watchVariant() {
        if (!this.source) return;

        this.variant = new MutationObserver(() => this.sync());
        this.variant.observe(this.source, { childList: true, subtree: true, characterData: true });
        this.sync();
      }

      sync() {
        if (!this.source || !this.priceTarget) return;
        const regular = this.source.querySelector('.price-item--sale, .price-item--regular');
        const price = regular?.textContent.trim();
        if (price) this.priceTarget.textContent = price;
      }
    }
  );
}

/* Description clamp. Applied here rather than in CSS so that the toggle only
   appears when the text is actually long enough to need one. */
(function clampDescription() {
  const MOBILE = '(max-width: 749px)';
  const LINES = 5;

  function setup() {
    const description = document.querySelector('.product__description');
    if (!description || description.dataset.clamped) return;

    const body = document.createElement('div');
    body.className = 'product__description-text';
    while (description.firstChild) body.appendChild(description.firstChild);
    description.appendChild(body);
    description.dataset.clamped = 'true';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'product__description-toggle';
    toggle.hidden = true;
    description.appendChild(toggle);

    let open = false;

    const label = () => {
      toggle.textContent = open ? 'Read less' : 'Read more';
      toggle.setAttribute('aria-expanded', String(open));
    };

    toggle.addEventListener('click', () => {
      open = !open;
      description.classList.toggle('product__description--clamped', !open);
      label();
    });

    const measure = () => {
      if (!window.matchMedia(MOBILE).matches) {
        description.classList.remove('product__description--clamped');
        toggle.hidden = true;
        return;
      }

      // Measure unclamped, then clamp only if the text overflows.
      description.classList.remove('product__description--clamped');
      const lineHeight = parseFloat(getComputedStyle(body).lineHeight) || 0;
      const overflows = lineHeight > 0 && body.scrollHeight > lineHeight * LINES + 1;

      toggle.hidden = !overflows;
      if (overflows && !open) description.classList.add('product__description--clamped');
      if (overflows) label();
    };

    measure();
    window.addEventListener('resize', measure);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

/* Desktop: lift the long-form content out of the sticky column.

   The info column is sticky so Add to cart stays on screen while the gallery
   scrolls, but a sticky element taller than the window simply hangs off the
   bottom of it - which is what put the button out of reach. Moving the
   description, accordions and sizing note below the grid brings the column
   back under one screen, and reads better anyway: the detail belongs after
   the decision, not squeezed beside it.

   The theme gives these blocks no shared wrapper, so the move happens here.
   Everything is returned to its original position below the breakpoint. */
(function detailsBand() {
  var DESKTOP = '(min-width: 990px)';
  var SELECTOR = '.product__description, .product__accordion, .product__info-container > .product__text';

  function setup() {
    var info = document.querySelector('.product__info-container');
    var grid = document.querySelector('.product.grid');
    if (!info || !grid || grid.dataset.detailsBand) return;
    grid.dataset.detailsBand = 'true';

    var nodes = Array.prototype.slice.call(info.querySelectorAll(SELECTOR));
    if (!nodes.length) return;

    // Remember where each one came from so the move is reversible.
    var anchors = nodes.map(function (node) {
      var mark = document.createComment('product-details');
      node.parentNode.insertBefore(mark, node);
      return mark;
    });

    var band = document.createElement('div');
    band.className = 'product__details';

    var query = window.matchMedia(DESKTOP);
    var moved = false;

    function apply() {
      if (query.matches && !moved) {
        nodes.forEach(function (node) {
          band.appendChild(node);
        });
        grid.parentNode.insertBefore(band, grid.nextSibling);
        moved = true;
      } else if (!query.matches && moved) {
        nodes.forEach(function (node, i) {
          anchors[i].parentNode.insertBefore(node, anchors[i].nextSibling);
        });
        if (band.parentNode) band.parentNode.removeChild(band);
        moved = false;
      }
    }

    apply();

    if (query.addEventListener) {
      query.addEventListener('change', apply);
    } else {
      query.addListener(apply);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // The theme editor re-renders the section without reloading the page.
  document.addEventListener('shopify:section:load', function () {
    var grid = document.querySelector('.product.grid');
    if (grid) delete grid.dataset.detailsBand;
    setup();
  });
})();
